import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Award,
  BookOpen,
  Calendar,
  Building,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  GraduationCap,
  Loader2,
  Mail,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useFormSubmit } from '../../hooks/useFormSubmit';
import FormNotification from '../../components/FormNotification';

const fallbackData = {
  titulo: 'Diplomados Especializantes',
  descripcion: '',
  calendarios: [],
  diplomados: [],
};

const DiplomadoDetalle = () => {
  const { slug } = useParams();
  const [data, setData] = useState(fallbackData);
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { loading: isSubmitting, success: isSuccess, error: submitError, submitForm, resetForm } = useFormSubmit('diplomados-especializantes');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    pais: '',
    institucion: '',
    mensaje: ''
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const version = Date.now();
        const [response, generalesResponse] = await Promise.all([
          fetch(`/data/diplomados-especializantes.json?v=${version}`, { cache: 'no-store' }),
          fetch(`/data/generales.json?v=${version}`, { cache: 'no-store' }),
        ]);

        if (!response.ok) {
          throw new Error('No se pudo cargar la información de diplomados.');
        }

        const [json, generalesJson] = await Promise.all([
          response.json(),
          generalesResponse.ok ? generalesResponse.json() : Promise.resolve(null),
        ]);

        setData(json);
        setDatosBancarios(generalesJson?.datosBancarios || null);
        setError(null);
      } catch (err) {
        console.error('Error cargando diplomados:', err);
        setError(err.message || 'Error al cargar los diplomados.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const diplomado = useMemo(
    () => data.diplomados.find((item) => item.slug === slug),
    [data.diplomados, slug]
  );

  const costosInversion = useMemo(() => {
    const inversion = diplomado?.inversion || {};
    const incluyeGeneral = Array.isArray(diplomado?.incluye) ? diplomado.incluye : [];
    const incluyeFinalGeneral = diplomado?.incluye_final || '';

    if (Array.isArray(inversion.costos) && inversion.costos.length > 0) {
      return inversion.costos.map((costo) => ({
        ...costo,
        incluye: Array.isArray(costo.incluye) && costo.incluye.length > 0 ? costo.incluye : incluyeGeneral,
        incluyeFinal: costo.incluyeFinal || incluyeFinalGeneral,
      }));
    }

    return [
      {
        textoHtml: 'Con membresía <a href="https://cenid.org/redriel" target="_blank" rel="noopener noreferrer" class="underline hover:text-white">Riel</a> / <a href="https://cenid.org/redunadi" target="_blank" rel="noopener noreferrer" class="underline hover:text-white">RedUnadi</a>',
        monto: inversion.conMembresia,
        destacado: true,
        incluye: incluyeGeneral,
        incluyeFinal: incluyeFinalGeneral,
      },
      {
        texto: 'Sin membresía',
        monto: inversion.sinMembresia,
        incluye: incluyeGeneral,
        incluyeFinal: incluyeFinalGeneral,
      },
    ].filter((costo) => costo.monto || costo.texto || costo.textoHtml);
  }, [diplomado]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await submitForm({
      ...formData,
      diplomado: diplomado?.nombre || '',
    });

    if (result.success) {
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        pais: '',
        institucion: '',
        mensaje: ''
      });

      setTimeout(() => {
        resetForm();
      }, 5000);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-700">Cargando diplomado...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center px-4">
          <p className="text-red-600 font-semibold mb-2">{error}</p>
          <Link to="/formacion" className="text-[#0b742f] hover:underline">
            Volver a Formación
          </Link>
        </div>
      </div>
    );
  }

  if (!diplomado) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center px-4">
          <p className="text-gray-900 font-semibold mb-2">Diplomado no encontrado.</p>
          <Link to="/formacion" className="text-[#0b742f] hover:underline">
            Volver a Formación
          </Link>
        </div>
      </div>
    );
  }

  const justificacion = Array.isArray(diplomado.justificacion)
    ? diplomado.justificacion
    : diplomado.justificacion
      ? [diplomado.justificacion]
      : [];

  const trabajoFinalItems = Array.isArray(diplomado.trabajoFinal)
    ? diplomado.trabajoFinal
    : Array.isArray(diplomado.proyectoFinal)
      ? diplomado.proyectoFinal
      : diplomado.trabajoFinal || diplomado.proyectoFinal
        ? [diplomado.trabajoFinal || diplomado.proyectoFinal]
        : [];

  const articulo7Equivalencias = [
    'I. Los textos que son productos de investigacion registrados como capitulos de un libro, seran considerados con el mismo valor que un articulo, siempre y cuando garanticen procesos rigurosos de evaluacion por pares y cuyos contenidos siguen parametros de integridad academica y etica de publicacion cientifica.',
    'II. Dos productos de investigacion registrados como reportes, informes con resultados de evaluaciones, propuestas o estrategias de politica publica, propuestas de mejoras de programas gubernamentales, estrategias sociales, normatividad o reglamentos, antologia, prologo o estudio introductorio, dossier o numero tematico, seran contabilizados con el mismo valor que un articulo, a condicion de que cuyos contenidos siguen parametros de integridad academica y etica de publicacion cientifica.',
    'III. Un producto de investigacion registrado como libro, sera contabilizado con el mismo valor que cuatro articulos, a condicion de que garanticen procesos rigurosos de evaluacion por pares y cuyos contenidos siguen parametros de integridad academica y etica de publicacion cientifica.',
    'IV. Los productos tecnologicos registrados como desarrollos tecnologicos, seran equivalentes a un articulo cientifico; mientras que una transferencia tecnologica o un derecho de propiedad intelectual otorgado seran equivalentes a cuatro articulos.',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero */}
      <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center" />
        <div className="relative max-w-7xl mx-auto px-6">
          <Link
            to="/formacion"
            className="inline-flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Formación
          </Link>
          <div className="flex items-start gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl shrink-0">
              <GraduationCap className="w-12 h-12 text-white" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-1.5 rounded-full mb-4 text-sm font-medium">
                <Award className="w-4 h-4" />
                Diplomado Especializante
              </div>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-4">{diplomado.nombre}</h1>
              <p className="text-green-100 text-lg max-w-4xl">{diplomado.resumen}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Quick Info Cards — floats up over hero */}
        <div className="grid md:grid-cols-3 gap-6 -mt-12 mb-16 relative z-10">
          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#0b742f] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-2 rounded-lg">
                <Clock className="w-5 h-5 text-[#0b742f]" />
              </div>
              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Duración y carga horaria</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{diplomado.duracion} / {diplomado.cargaHoraria}</p>
          </div>
          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#e87729] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#e87729]/10 p-2 rounded-lg">
                <BookOpen className="w-5 h-5 text-[#e87729]" />
              </div>
              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Metodología</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{diplomado.metodologia}</p>
          </div>
          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-blue-600 hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-600/10 p-2 rounded-lg">
                <GraduationCap className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Modalidad</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{diplomado.modalidad}</p>
          </div>
        </div>

        {/* Justificación */}
        {justificacion.length > 0 && (
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b742f]">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">¿Por qué este diplomado?</h2>
              <div className="space-y-4">
                {justificacion.map((parrafo, idx) => (
                  <p key={idx} className="text-gray-700 leading-relaxed text-lg">
                    {parrafo}
                  </p>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Objetivo General */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-white/20 p-3 rounded-xl">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Objetivo General</h2>
            </div>
            <p className="text-green-50 text-lg leading-relaxed max-w-5xl">{diplomado.objetivo}</p>
          </div>
        </section>

        {/* Impacto y Valor en el SNII */}
        {diplomado.impactoSnii && diplomado.impactoSnii.length > 0 && (
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12  border-l-4 border-[#0b742f]">
              <div className="flex items-center gap-3 mb-8">
                <TrendingUp className="w-7 h-7 text-[#0b742f]" />
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Impacto y Valor en el SNII</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {diplomado.impactoSnii.map((item) => (
                  <div
                    key={item.numero}
                    className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="bg-[#0b742f]/10 w-12 h-12 rounded-full flex items-center justify-center shrink-0">
                        <span className="text-[#0b742f] font-bold text-lg">{item.numero}</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">{item.titulo}</h3>
                        <p className="text-gray-600 leading-relaxed text-sm">{item.descripcion}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Criterios especificos de evaluacion del SNII - Articulo 7 */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b742f]">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-7 h-7 text-[#0b742f]" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                Criterios especificos de evaluacion del Sistema Nacional de Investigadores e Investigadoras SNII
              </h2>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              Articulo 7. Para reconocer la produccion de la investigacion de manera integral, se consideraran las siguientes equivalencias:
            </p>

            <ul className="space-y-4 mb-6">
              {articulo7Equivalencias.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-[#0b742f] mt-0.5 shrink-0" />
                  <span className="text-gray-700 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            <p className="text-sm text-gray-600">
              Fuente oficial:{' '}
              <a
                href="https://www.dof.gob.mx/nota_detalle.php?codigo=5785593&fecha=22/04/2026"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0b742f] font-semibold hover:underline"
              >
                Diario Oficial de la Federacion (DOF)
              </a>
            </p>
          </div>
        </section>

        {/* Módulos */}
        {diplomado.modulos && diplomado.modulos.length > 0 && (
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12  border-l-4 border-[#0b742f]">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Contenido del Programa</h2>
              <p className="text-sm text-gray-500 font-medium uppercase tracking-wide mb-1">Estructura</p>
              <p className="text-gray-800 font-semibold">{diplomado.estructura}</p>
              <div className="space-y-6 mt-6">
                {diplomado.modulos.map((modulo) => (
                  <div
                    key={modulo.numero}
                    className="border border-gray-100 rounded-xl overflow-hidden hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="bg-[#0b742f]/5 border-l-4 border-[#0b742f] px-6 py-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-[#0b742f] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                          {modulo.numero}
                        </div>
                        <h3 className="font-bold text-gray-900">{modulo.titulo}</h3>
                      </div>
                      <span className="text-sm font-semibold text-[#0b742f] whitespace-nowrap bg-[#0b742f]/10 px-3 py-1 rounded-full">
                        {modulo.horas} hrs
                      </span>
                    </div>
                    {modulo.temas && modulo.temas.length > 0 && (
                      <div className="px-6 py-4 space-y-2">
                        {modulo.temas.map((tema, tIdx) => (
                          <div key={tIdx} className="flex items-start gap-3">
                            <CheckCircle className="w-4 h-4 text-[#0b742f] mt-0.5 shrink-0" />
                            <p className="text-gray-700 text-sm leading-relaxed">{tema}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Trabajo / Evaluación Final */}
        {trabajoFinalItems.length > 0 && (
          <section className="mb-16">
            <div className="bg-gradient-to-br from-[#e87729] to-[#d66820] text-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-white/20 p-3 rounded-xl">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold">Trabajo / Evaluación Final</h2>
              </div>
              <ul className="space-y-3">
                {trabajoFinalItems.map((item, idx) => (
                  <li key={idx} className="text-orange-50 text-lg leading-relaxed flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-1 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Calendarios */}
        {(data.calendarios || []).length > 0 && (
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b742f]">
              <div className="flex items-center gap-3 mb-8">
                <Calendar className="w-7 h-7 text-[#0b742f]" />
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Calendarios</h2>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {data.calendarios.map((calendario) => (
                  <div
                    key={calendario.nombre}
                    className="bg-gradient-to-br from-[#0b742f]/5 to-[#e87729]/10 border border-[#0b742f]/20 rounded-xl p-6"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{calendario.nombre}</h3>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Periodo de inscripción</p>
                        <p className="font-semibold text-gray-900">{calendario.periodoInscripcion}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Inicio - Fin</p>
                        <p className="font-semibold text-gray-900">{calendario.inicioFin}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
        
        {/* Inversión e inclusiones por costo */}
        <section className="mb-16">
          <div className="mb-6 flex items-center gap-3">
            <DollarSign className="w-7 h-7 text-[#e87729]" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              {diplomado.inversion?.titulo || 'Inversión por participante'}
            </h2>
          </div>
          <div className={`grid gap-8 ${costosInversion.length > 1 ? 'xl:grid-cols-2' : 'grid-cols-1 max-w-3xl'}`}>
            {costosInversion.map((costo, index) => (
              <div
                key={`${costo.texto || costo.textoHtml || 'costo'}-${index}`}
                className={`rounded-2xl shadow-xl overflow-hidden ${
                  costo.destacado || index === 0
                    ? 'bg-gradient-to-br from-[#e87729] to-[#d66820] text-white'
                    : 'bg-white border border-orange-200'
                }`}
              >
                <div className="p-8">
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                      {costo.textoHtml ? (
                        <p
                          className={`${costo.destacado || index === 0 ? 'text-orange-100' : 'text-[#e87729]'} text-sm mb-2`}
                          dangerouslySetInnerHTML={{ __html: costo.textoHtml }}
                        />
                      ) : (
                        costo.texto && (
                          <p className={`${costo.destacado || index === 0 ? 'text-orange-100' : 'text-[#e87729]'} text-sm mb-2`}>
                            {costo.texto}
                          </p>
                        )
                      )}
                      {costo.monto && (
                        <p className={`${costo.destacado || index === 0 ? 'text-white' : 'text-gray-900'} text-3xl md:text-4xl font-bold`}>
                          {costo.monto}
                        </p>
                      )}
                    </div>
                    <div className={`rounded-full p-3 ${costo.destacado || index === 0 ? 'bg-white/15' : 'bg-[#e87729]/10'}`}>
                      <DollarSign className={`w-6 h-6 ${costo.destacado || index === 0 ? 'text-white' : 'text-[#e87729]'}`} />
                    </div>
                  </div>

                  {costo.descripcion && (
                    <p className={`${costo.destacado || index === 0 ? 'text-orange-50' : 'text-gray-600'} text-sm mb-6 leading-relaxed`}>
                      {costo.descripcion}
                    </p>
                  )}

                  <div className={`rounded-2xl p-6 ${costo.destacado || index === 0 ? 'bg-white/10' : 'bg-orange-50/70 border border-orange-100'}`}>
                    <h3 className={`text-lg font-bold mb-5 flex items-center gap-2 ${costo.destacado || index === 0 ? 'text-white' : 'text-gray-900'}`}>
                      <CheckCircle className={`w-5 h-5 ${costo.destacado || index === 0 ? 'text-white' : 'text-[#0b742f]'}`} />
                      Este costo incluye
                    </h3>

                    <ul className="space-y-3">
                      {(costo.incluye || []).map((item, idx) => (
                        <li
                          key={idx}
                          className={`flex items-start gap-3 rounded-lg p-3 ${
                            costo.destacado || index === 0 ? 'bg-white/10' : 'bg-white'
                          }`}
                        >
                          <CheckCircle className={`w-5 h-5 mt-0.5 shrink-0 ${costo.destacado || index === 0 ? 'text-white' : 'text-[#0b742f]'}`} />
                          <span className={`${costo.destacado || index === 0 ? 'text-white/95' : 'text-gray-700'} text-sm leading-relaxed`}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {costo.incluyeFinal && (
                      <p className={`mt-5 font-bold leading-relaxed ${costo.destacado || index === 0 ? 'text-white' : 'text-gray-800'}`}>
                        {costo.incluyeFinal}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Cuenta Bancaria */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-[#0b742f]">
            <div className="flex items-center gap-3 mb-6">
              <Building className="w-7 h-7 text-[#0b742f]" />
              <h2 className="text-2xl font-bold text-gray-900">Datos Bancarios para Pagos</h2>
            </div>

            {datosBancarios ? (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Beneficiario</p>
                    <p className="text-lg font-semibold text-gray-900">{datosBancarios.titular}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Banco</p>
                    <p className="text-lg font-semibold text-gray-900">{datosBancarios.banco}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Cuenta</p>
                    <p className="text-lg font-semibold text-gray-900 font-mono">{datosBancarios.numeroCuenta}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">CLABE Interbancaria</p>
                    <p className="text-lg font-semibold text-gray-900 font-mono">{datosBancarios.clabe}</p>
                  </div>
                  {datosBancarios.sucursal && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Sucursal</p>
                      <p className="text-lg font-semibold text-gray-900">{datosBancarios.sucursal}</p>
                    </div>
                  )}
                  {datosBancarios.swift && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">SWIFT</p>
                      <p className="text-lg font-semibold text-gray-900 font-mono">{datosBancarios.swift}</p>
                    </div>
                  )}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <p className="text-sm text-blue-900">
                      <strong>Importante:</strong> Envía tu comprobante de pago al correo{' '}
                      <a
                        href={`mailto:${diplomado.contacto}`}
                        className="text-blue-600 hover:underline font-semibold"
                      >
                        {diplomado.contacto}
                      </a>
                      {' '}para confirmar tu inscripción.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center py-4">
                <Loader2 className="animate-spin w-5 h-5 text-gray-400" />
                <span className="ml-2 text-gray-500">Cargando datos bancarios...</span>
              </div>
            )}
          </div>
        </section>

        {/* Formulario de Inscripción */}
        <section className="mb-16">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Mail className="w-8 h-8 text-[#0b742f]" />
                  Formulario de Inscripción
                </h2>
                <p className="text-gray-600 mt-2">Completa tus datos para iniciar tu proceso en este diplomado.</p>
                <div className="h-1 w-32 bg-[#e87729] rounded-full mt-3"></div>
              </div>

              {isSuccess && (
                <FormNotification
                  type="success"
                  message="¡Solicitud enviada exitosamente! Nos pondremos en contacto contigo pronto."
                  onClose={resetForm}
                />
              )}

              {submitError && (
                <FormNotification
                  type="error"
                  message={submitError}
                  onClose={resetForm}
                />
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre completo *</label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Tu nombre completo"
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Correo electrónico *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="tu@email.com"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Teléfono *</label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="+52 123 456 7890"
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">País *</label>
                    <input
                      type="text"
                      name="pais"
                      value={formData.pais}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="México"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Institución *</label>
                  <input
                    type="text"
                    name="institucion"
                    value={formData.institucion}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Universidad o institución"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mensaje</label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Cuéntanos sobre tu interés en el diplomado..."
                    disabled={isSubmitting}
                  ></textarea>
                </div>

                <div className="flex flex-wrap gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Enviar solicitud
                      </>
                    )}
                  </button>

                  <a
                    href={`mailto:${diplomado.contacto}?subject=Información sobre ${encodeURIComponent(diplomado.nombre)}`}
                    className="bg-gradient-to-r from-[#e87729] to-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
                  >
                    <Mail className="w-5 h-5" />
                    Más información
                  </a>
                </div>
              </form>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default DiplomadoDetalle;
